import React, { useState, useContext, useEffect } from 'react';
import { TextButton, SvgButton, Spinner, A } from 't-lib-ui/widgets';
import { sanitizePlainText, sanitizeLongText } from 't-lib-ui/connector';
import { HelpPopup, getKeyHandler } from 't-lib-ui/structure';
import { RouterContext, goTo } from 't-lib-ui/router';
import { RestCatalog, RestFeedSummary, RestUserFeed } from '../schema-copy/api-schema';
import { getCachedFeedSummaries } from '../connector/feed-summaries';
import { nonNull } from 't-lib-ui/helpers';
import { formatLastPostTime, formatFrequency, formatCategory, formatPostCount } from '../utils/formatting';
import { AppState } from '../context/general';
import { CategoryIcon } from './CategoryIcons';
import { useFeedNameOverride } from '../connector/feed-name-overrides';
import CheckmarkCircleIcon from './CheckmarkCircleIcon';
import FeedInfoPopup from './FeedInfoPopup';
import FlagIcon from './FlagIcon';
import { useReport } from './ReportPopup';
import PrivateFeedIcon from './PrivateFeedIcon';
import MailIcon from './MailIcon';
import FeedIssueIcon from './FeedIssueIcon';
import FeedIssuePopup from './FeedIssuePopup';
import { hasFeedIssue } from '../utils/feedIssue';
import { postStatsBadges } from '../utils/postStatsBadges';
import FeedIcon from './FeedIcon';
import { getFeedIconUrl } from '../schema-copy/feed-icon';
import { getDefaultIconUrl } from '../schema-copy/default-icon';
import { requestStartCollapsed } from './ThreeColumnLayout';
import { useFeedQuota } from '../connector/feed-quota';
import FeedQuotaNote from './FeedQuotaNote';
import AdminCategoryPopup from './admin/AdminCategoryPopup';
import './FeedTile.css';

interface FeedTileProps {
  feed: RestUserFeed | RestCatalog;
  subscribed: boolean;
  onAction?: () => void;
  onPreview?: () => void;
  onView?: () => void;
  onQuotaExceeded?: () => void;
  showPrivacyNote?: boolean;
  isAdmin?: boolean;
  children?: React.ReactNode;
}

const FeedTile: React.FC<FeedTileProps> = ({ feed, subscribed, onAction, onPreview, onView, onQuotaExceeded, showPrivacyNote = false, isAdmin = false, children }) => {
  const appState = useContext(AppState);
  const { feedEngine } = appState;
  const routerCtx = useContext(RouterContext);
  const customFeedName = useFeedNameOverride(feed.id);
  const { report, reportPopup } = useReport();
  const { atLimit: atFeedQuota } = useFeedQuota();
  // Block following a new feed once the quota is reached; unfollowing is always allowed.
  const followBlocked = atFeedQuota && !subscribed;

  // Children/parent are only present on catalog entries
  const catalog = feed.objectType == 'catalog' ? feed as RestCatalog : null;
  const childIds = catalog?.children;
  const parentId = catalog?.parent;

  const [groupSummaries, setGroupSummaries] = useState<Record<string, RestFeedSummary>>({});

  useEffect(() => {
    const ids = [...(childIds ?? []), ...(parentId ? [parentId] : [])];
    if (!ids.length || !appState.state.authData) {
      setGroupSummaries({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const summaries = await getCachedFeedSummaries(nonNull(appState.state.authData), ids.slice(0, 31));
        if (cancelled) return;
        const map: Record<string, RestFeedSummary> = {};
        for (const s of summaries) map[s.id] = s;
        setGroupSummaries(map);
      } catch (e) {
        if (!cancelled) console.error('Failed to load feed group summaries:', e);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childIds?.join(','), parentId, appState.state.authData]);

  const childrenForDisplay = childIds?.map(id => groupSummaries[id]).filter((s): s is RestFeedSummary => !!s) ?? [];
  const parentSummary = parentId ? groupSummaries[parentId] : undefined;

  const [showInfoPopup, setShowInfoPopup] = useState(false);
  const [showPaidInfoPopup, setShowPaidInfoPopup] = useState(false);
  const [showPrivateInfoPopup, setShowPrivateInfoPopup] = useState(false);
  const [showIssuePopup, setShowIssuePopup] = useState(false);
  const [showCategoryPopup, setShowCategoryPopup] = useState(false);
  // Set after an admin changed the category, so the tile reflects it without reloading the list.
  const [categoryOverride, setCategoryOverride] = useState<string | undefined>(undefined);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [imgErrored, setImgErrored] = useState(false);
  const infoButtonId = `feed-info-btn-${feed.id}`;
  const paidButtonId = `feed-paid-btn-${feed.id}`;
  const privateButtonId = `feed-private-btn-${feed.id}`;
  const issueButtonId = `feed-issue-btn-${feed.id}`;
  const adminButtonId = `feed-admin-btn-${feed.id}`;
  const reportButtonId = `feed-report-btn-${feed.id}`;

  const handleActionClick = async () => {
    if (!feedEngine || isProcessing) return;

    // Clear any previous error
    setErrorMessage(null);
    setIsProcessing(true);

    try {
      const minDelayPromise = new Promise(resolve => setTimeout(resolve, 750));

      // Check feed engine state
      const engineState = feedEngine.getState();
      if (engineState != 'READY' && engineState != 'UPDATING' && engineState != 'INTERRUPTED') {
        await minDelayPromise;
        if (engineState == 'LOADING') {
          setErrorMessage('Please wait a moment and try again.');
        } else if (engineState == 'ERROR') {
          setErrorMessage('Failed to load your feeds. Please refresh the page.');
        } else {
          setErrorMessage(`Unable to follow. Please try again. (Feed engine state: ${engineState})`);
        }
        return;
      }

      // Perform the operation
      let success = false;
      if (subscribed) {
        success = await feedEngine.unsubscribe(feed.id);
      } else {
        // Hand the engine this catalog feed's metadata so it can subscribe without a server lookup
        // (essential for the anonymous engine, which otherwise can't resolve an unsubscribed feed).
        feedEngine.registerFeedInfo(feed);
        const result = await feedEngine.subscribe(feed.id);
        success = !!result;
      }

      await minDelayPromise;

      if (success) {
        onAction?.();
      } else {
        setErrorMessage(subscribed ? 'Failed to unfollow. Please try again.' : 'Failed to follow. Please try again.');
      }
    } catch (error) {
      if (error && typeof error == 'object' && 'type' in error && (error as any).type == 'Quota') {
        onQuotaExceeded?.();
      } else {
        console.error('Subscription action failed:', error);
        const errorMsg = error instanceof Error ? error.message : 'Unknown error';
        setErrorMessage(`Error: ${errorMsg}`);
      }

      // Wait for minimum delay even on error
      await new Promise(resolve => setTimeout(resolve, 750));
    } finally {
      setIsProcessing(false);
    }
  };

  const isCatalog = feed.objectType == 'catalog';
  const category = categoryOverride ?? feed.category;

  // Drop a stale local category if the tile gets recycled for another feed.
  useEffect(() => setCategoryOverride(undefined), [feed.id]);

  // The selected feed image (favicon / feed image / custom) at 96px-rendered (192px for retina),
  // falling back to the generated icon both when no image exists and if the CDN image fails to load.
  const imageUrl = imgErrored ? getDefaultIconUrl(feed.shortTitle ?? feed.title) : getFeedIconUrl(feed, 192);
  const siteHostname = (() => {
    if (!feed.siteUrl) return '';
    try {
      return new URL(feed.siteUrl).hostname;
    } catch {
      return feed.siteUrl;
    }
  })();

  return (
    <div className="feedTile">
      <div className="feedTileHeader withImage">
        <img
          src={imageUrl}
          alt={feed.title}
          className={`feedTileImage${feed.circularIcon ? ' feedIconCircular' : ''}`}
          onError={() => { if (!imgErrored) setImgErrored(true); }}
        />
        <div className="feedTileHeaderBody">
          <div className="feedTileHeaderTop">
            <div className="feedTileHeaderText">
              <h3 className="feedTileTitle">{customFeedName ?? feed.shortTitle ?? feed.title}</h3>
              {feed.siteUrl && (
                <p className="feedTileUrl">
                  <A href={feed.siteUrl} target="_blank" rel="noopener noreferrer">
                    {siteHostname}
                  </A>
                </p>
              )}
            </div>
            <div className="feedTileHeaderTools">
              {feed.feedStatus == 'NEWSLETTER' && (
                <span className="feedTilePrivateLock" title="Newsletter feed" style={{ display: 'inline-flex', alignItems: 'center' }}>
                  <MailIcon size={22} color="currentColor" />
                </span>
              )}
              {feed.feedStatus == 'PRIVATE' && (
                <SvgButton
                  focusId={privateButtonId}
                  className="feedTilePrivateLock"
                  onClick={() => setShowPrivateInfoPopup(true)}
                  title="Private feed"
                  type="minor"
                  size="medium"
                >
                  <PrivateFeedIcon size={22} color="var(--error-color)" />
                </SvgButton>
              )}
              {hasFeedIssue(feed) && (
                <SvgButton
                  focusId={issueButtonId}
                  className="feedTilePrivateLock"
                  onClick={() => setShowIssuePopup(true)}
                  title="This feed has a problem"
                  type="minor"
                  size="medium"
                >
                  <FeedIssueIcon size={20} color="var(--error-color)" />
                </SvgButton>
              )}
              {isAdmin && (
                <TextButton
                  focusId={adminButtonId}
                  onClick={() => goTo(routerCtx, isCatalog ? `/a/admin/catalog/${feed.id}` : `/a/admin/feed/${feed.id}`)}
                  label="A"
                  className='adminButton'
                  size='small'
                  type='borderless'
                />
              )}
              {feed.paid === true && (
                <TextButton
                  focusId={paidButtonId}
                  onClick={() => setShowPaidInfoPopup(true)}
                  label="$"
                  className='dollarButton'
                  size='small'
                  type='borderless'
                />
              )}
              <SvgButton
                focusId={reportButtonId}
                className="feedTileReportIcon"
                onClick={() => report({ feedId: feed.id, title: feed.shortTitle ?? feed.title })}
                title="Report this feed"
                type="minor"
                size="medium"
              >
                <FlagIcon size={18} />
              </SvgButton>
              <SvgButton
                focusId={infoButtonId}
                className="feedTileInfoIcon"
                onClick={() => setShowInfoPopup(true)}
                title="Show feed details"
                type="minor"
                size="medium"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="16" x2="12" y2="12"/>
                  <circle cx="12" cy="8" r="0.5" fill="currentColor"/>
                </svg>
              </SvgButton>
            </div>
          </div>
          {(feed.description || ('catalogDescription' in feed && feed.catalogDescription)) && (
            <div className="feedTileDescriptions">
              {feed.description && (
                <p className="feedTileDescription">
                  {sanitizeLongText(sanitizePlainText(feed.description), 200)}
                </p>
              )}
              {'catalogDescription' in feed && feed.catalogDescription && (
                <p className="feedTileCatalogDescription">
                  {feed.catalogDescription}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      <HelpPopup
        show={showPaidInfoPopup}
        title="Paid Feeds"
        onClose={() => setShowPaidInfoPopup(false)}
        closable={true}
        closeFocus={paidButtonId}
        className="feedTilePaidInfoPopup"
      >
        <div>
          This site charges for access to most content. Adding the feed to Downread is free, 
          and you can browse article titles and summaries at no cost.
        </div>
        <div style={{ marginTop: '12px' }}>
          However, to read the full articles, you may need to subscribe directly on the 
          publisher's website. Downread does not charge for accessing feeds – any subscription 
          fees are paid directly to the content publisher.
        </div>
      </HelpPopup>

      <HelpPopup
        show={showPrivateInfoPopup}
        title="Private Feed"
        onClose={() => setShowPrivateInfoPopup(false)}
        closable={true}
        closeFocus={privateButtonId}
      >
        <div>
          This is a private feed. It will not be published or shown to other users.
        </div>
        <div style={{ marginTop: '12px' }}>
          Only users who know the exact feed URL can add it to their own account.
          Private feeds are intended for personal subscriptions such as paid newsletters
          or feeds with personalized content.
        </div>
      </HelpPopup>

      <FeedIssuePopup
        show={showIssuePopup}
        fetchErrorCount={feed.fetchErrorCount}
        fetchFailingSinceAt={feed.fetchFailingSinceAt}
        feedTitle={feed.shortTitle ?? feed.title}
        closeFocus={issueButtonId}
        onClose={() => setShowIssuePopup(false)}
      />

      <FeedInfoPopup
        feed={showInfoPopup ? feed : null}
        onClose={() => setShowInfoPopup(false)}
        closeFocus={infoButtonId}
      />

      {reportPopup}

      {isAdmin && (
        <AdminCategoryPopup
          show={showCategoryPopup}
          feedId={feed.id}
          isCatalog={isCatalog}
          category={category}
          onClose={() => setShowCategoryPopup(false)}
          onChanged={setCategoryOverride}
        />
      )}

      {feed.downreadPublicComment && (
        <p className="feedTileComment">
          {feed.downreadPublicComment}
        </p>
      )}

      <div className="feedTileMetadata">
        {/* Admins can retune the category straight from the tile; everyone else sees plain text.
            The button also shows without a category, so a cleared one can be set again. */}
        {isAdmin ? (
          <button
            type="button"
            className="feedTileMetaItem feedTileMetaCategory feedTileMetaCategoryButton flowFocussable"
            tabIndex={0}
            title="Change category"
            onKeyDown={getKeyHandler({ enterBehaviour: 'action', spaceBehaviour: 'action', sidewaysBehaviour: 'both', onAction: (el: any) => el.click() }) as any}
            onClick={() => setShowCategoryPopup(true)}
          >
            {category && <CategoryIcon category={category} size={16} color="var(--gray-400)" />}
            {(category && formatCategory(category)) || 'Set category'}
          </button>
        ) : category && (
          <span className="feedTileMetaItem feedTileMetaCategory">
            <CategoryIcon category={category} size={16} color="var(--gray-400)" />
            {formatCategory(category)}
          </span>
        )}
        {feed.postCount > 0 && feed.postCount < 100 && (
          <span className="feedTileMetaItem"><strong>{formatPostCount(feed.postCount)}</strong> available</span>
        )}
        {/* What a typical post carries (words, images, videos, audio), from the sync server's
            per-post averages. Absent on feeds that haven't been analyzed yet. */}
        {postStatsBadges(feed.postStats).map(badge => (
          <span key={badge} className="feedTileMetaItem feedTileContentBadge">{badge}</span>
        ))}
        {(() => {
          // Only show post frequency if feed has been in catalog for at least 48h
          if (!feed.postFrequency) return null;
          // Check if this is a catalog entry (has dateAdded field)
          if (!('dateAdded' in feed)) return null;
          const dateAdded = new Date(feed.dateAdded).getTime();
          const fortyEightHoursAgo = Date.now() - (48 * 60 * 60 * 1000);
          if (dateAdded < fortyEightHoursAgo) {
            return <span className="feedTileMetaItem">{formatFrequency(feed.postFrequency)}</span>;
          }
          return null;
        })()}
        {feed.lastNewPostPubDate && (
          <span className="feedTileMetaItem">Last post {formatLastPostTime(feed.lastNewPostPubDate)}</span>
        )}
      </div>

      {childrenForDisplay.length > 0 && (
        <div className="feedTileChildren">
          <div className="feedTileChildrenLabel">Collection:</div>
          <ul className="feedTileChildList">
            {childrenForDisplay.map(c => (
              <li key={c.id}>
                <button
                  type="button"
                  className="feedTileChildItem flowFocussable"
                  tabIndex={0}
                  onKeyDown={getKeyHandler({ enterBehaviour: 'action', spaceBehaviour: 'action', sidewaysBehaviour: 'both', onAction: (el: any) => el.click() }) as any}
                  onClick={() => goTo(routerCtx, `/a/add/feed/${c.id}`)}
                >
                  <FeedIcon className="feedIcon" src={c.iconUrl} size={16} circular={c.circularIcon} fallback={<span className="feedIcon">📰</span>} />
                  <span className="feedName">{c.shortName}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {parentSummary && (
        <div className="feedTileParent">
          <span className="feedTileParentLabel">Part of </span>
          <button
            type="button"
            className="feedTileChildItem flowFocussable"
            tabIndex={0}
            onKeyDown={getKeyHandler({ enterBehaviour: 'action', spaceBehaviour: 'action', sidewaysBehaviour: 'both', onAction: (el: any) => el.click() }) as any}
            onClick={() => goTo(routerCtx, `/a/add/feed/${parentSummary.id}`)}
          >
            <FeedIcon className="feedIcon" src={parentSummary.iconUrl} size={16} circular={parentSummary.circularIcon} fallback={<span className="feedIcon">📰</span>} />
            <span className="feedName">{parentSummary.shortName}</span>
          </button>
        </div>
      )}

      {onAction && (
        <div className="feedTileAction">
          <Spinner spinning={isProcessing} className={isProcessing ? 'show' : ''} />
          {errorMessage && <span className="feedTileErrorText">{errorMessage}</span>}
          {followBlocked && !errorMessage && <FeedQuotaNote />}
          <div className="feedTileActionButtons">
            {subscribed && !isProcessing && (
              <span className="feedTileSubscribedIcon feedTileSubscribedIconInline" title="Following" aria-label="Following">
                <CheckmarkCircleIcon size={17} color="currentColor" />
              </span>
            )}
            <TextButton
              label={subscribed ? "Unfollow" : "+ Follow"}
              type="major"
              size="small"
              enabled={isProcessing || followBlocked ? "disabled" : "enabled"}
              onClick={handleActionClick}
              className="feedTileActionButton"
            />
            {subscribed ? (
              <TextButton
                label="View"
                type="major"
                size="small"
                focussable='sideway'
                onClick={() => {
                  requestStartCollapsed();
                  onView?.();
                  goTo(routerCtx, `/a/feed/${feed.id}`);
                }}
                className="feedTileActionButton"
              />
            ) : onPreview ? (
              <TextButton
                label="Preview"
                type="minor"
                size="small"
                focussable='sideway'
                onClick={onPreview}
                className="feedTileActionButton"
              />
            ) : null}
          </div>
        </div>
      )}

      {children && (
        <div className="feedTileExtra">
          {children}
        </div>
      )}

    </div>
  );
};

export default FeedTile;
